import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '~/auth';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Button, Paper, alpha } from '@mui/material';

import type { ThreadContextSnapshot } from './AiChatContext';
import { useAdminChatContextSnapshot } from './useAdminChatContextSnapshot';

const PANEL_DEFAULT_OFFSET = 16;
const PANEL_VIEWPORT_GUTTER = PANEL_DEFAULT_OFFSET * 2;
const DEFAULT_PANEL_WIDTH = 440;
const DEFAULT_PANEL_HEIGHT = 760;

type AiOpsChatEmbedMessage =
  | { type: 'ai-ops-chat:auth-token'; token: string }
  | { type: 'ai-ops-chat:context'; context: ThreadContextSnapshot };

type PanelPosition = {
  x: number;
  y: number;
};

function buildEmbedUrl(origin: string, contextSnapshot: ThreadContextSnapshot | null): string {
  const url = new URL('/embed/chat', origin);
  url.searchParams.set('source', 'admin');

  if (contextSnapshot?.customerId) {
    url.searchParams.set('customer_id', contextSnapshot.customerId);
  }
  if (contextSnapshot?.customerName) {
    url.searchParams.set('customer_name', contextSnapshot.customerName);
  }
  if (contextSnapshot?.orderId) {
    url.searchParams.set('order_id', contextSnapshot.orderId);
  }
  if (contextSnapshot?.department) {
    url.searchParams.set('department', contextSnapshot.department);
  }
  if (contextSnapshot?.mode) {
    url.searchParams.set('mode', contextSnapshot.mode);
  }
  if (contextSnapshot?.page) {
    url.searchParams.set('page', contextSnapshot.page);
  }

  return url.toString();
}

function clampPanelPosition(position: PanelPosition, panelWidth: number, panelHeight: number): PanelPosition {
  const maxX = Math.max(PANEL_DEFAULT_OFFSET, window.innerWidth - panelWidth - PANEL_DEFAULT_OFFSET);
  const maxY = Math.max(PANEL_DEFAULT_OFFSET, window.innerHeight - panelHeight - PANEL_DEFAULT_OFFSET);

  return {
    x: Math.min(Math.max(position.x, PANEL_DEFAULT_OFFSET), maxX),
    y: Math.min(Math.max(position.y, PANEL_DEFAULT_OFFSET), maxY),
  };
}

export default function AiOpsChatIframeDrawer() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const { getAccessToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);
  const contextSnapshot = useAdminChatContextSnapshot();
  const aiOpsChatOrigin = window.configs?.AI_OPS_CHAT_ORIGIN || null;
  const currentEmbedUrl = useMemo(
    () => (aiOpsChatOrigin ? buildEmbedUrl(aiOpsChatOrigin, contextSnapshot) : null),
    [aiOpsChatOrigin, contextSnapshot],
  );

  const postToIframe = useCallback(
    (message: AiOpsChatEmbedMessage) => {
      if (!aiOpsChatOrigin) {
        return;
      }

      iframeRef.current?.contentWindow?.postMessage(message, aiOpsChatOrigin);
    },
    [aiOpsChatOrigin],
  );

  const postAuthToken = useCallback(async () => {
    const token = await getAccessToken().catch(() => null);

    if (token) {
      postToIframe({ type: 'ai-ops-chat:auth-token', token });
    }
  }, [getAccessToken, postToIframe]);

  const postContext = useCallback(() => {
    if (contextSnapshot) {
      postToIframe({ type: 'ai-ops-chat:context', context: contextSnapshot });
    }
  }, [contextSnapshot, postToIframe]);

  const postAuthTokenAndContext = useCallback(() => {
    void postAuthToken();
    postContext();
  }, [postAuthToken, postContext]);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (!iframeSrc && currentEmbedUrl) {
      setIframeSrc(currentEmbedUrl);
    }
  }, [currentEmbedUrl, iframeSrc]);

  useEffect(() => {
    void postAuthToken();
  }, [postAuthToken]);

  useEffect(() => {
    postContext();
  }, [postContext]);

  useEffect(() => {
    if (!aiOpsChatOrigin) {
      return undefined;
    }

    function handleMessage(event: MessageEvent) {
      if (event.origin !== aiOpsChatOrigin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (event.data?.type === 'ai-ops-chat:close') {
        setIsOpen(false);
        return;
      }

      if (event.data?.type === 'ai-ops-chat:drag-start') {
        if (!panelRef.current) return;
        const panelBounds = panelRef.current.getBoundingClientRect();
        setPanelPosition({ x: panelBounds.left, y: panelBounds.top });
        return;
      }

      if (event.data?.type === 'ai-ops-chat:drag-move') {
        const movementX = Number(event.data.movementX) || 0;
        const movementY = Number(event.data.movementY) || 0;
        if (!panelRef.current || (!movementX && !movementY)) {
          return;
        }

        const panelBounds = panelRef.current.getBoundingClientRect();
        setPanelPosition((currentPosition) =>
          clampPanelPosition(
            {
              x: (currentPosition?.x ?? panelBounds.left) + movementX,
              y: (currentPosition?.y ?? panelBounds.top) + movementY,
            },
            panelBounds.width,
            panelBounds.height,
          ),
        );
        return;
      }

      if (event.data?.type === 'ai-ops-chat:ready') {
        postAuthTokenAndContext();
      }

      if (event.data?.type === 'ai-ops-chat:auth-required') {
        void postAuthToken();
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [aiOpsChatOrigin, postAuthToken, postAuthTokenAndContext]);

  if (!currentEmbedUrl) {
    return null;
  }

  return (
    <>
      {!isOpen ? (
        <Button
          variant="contained"
          size="small"
          onClick={openChat}
          aria-label="Open AI chat"
          disableElevation
          sx={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 99999,
            width: 32,
            minWidth: 32,
            minHeight: 92,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            py: 1,
            px: 0.25,
            overflow: 'hidden',
            borderRadius: '12px 0 0 12px',
            boxShadow: (theme) =>
              `0 14px 32px -14px ${alpha(theme.palette.primary.main, 0.75)}, 0 4px 10px -4px ${alpha(
                theme.palette.primary.main,
                0.45,
              )}`,
            background: (theme) =>
              `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 46%, ${theme.palette.primary.dark} 100%)`,
            transition: (theme) =>
              theme.transitions.create(['box-shadow', 'filter', 'transform', 'width', 'min-width'], {
                duration: theme.transitions.duration.shorter,
              }),
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(255,255,255,0.2), transparent 44%)',
              pointerEvents: 'none',
            },
            '&:hover': {
              filter: 'brightness(1.05)',
              transform: 'translateY(-50%)',
              width: 36,
              minWidth: 36,
              boxShadow: (theme) =>
                `0 18px 36px -14px ${alpha(theme.palette.primary.main, 0.85)}, 0 6px 14px -4px ${alpha(
                  theme.palette.primary.main,
                  0.5,
                )}`,
            },
            '&:focus-visible': {
              outline: (theme) => `2px solid ${alpha(theme.palette.primary.light, 0.8)}`,
              outlineOffset: 2,
            },
            '& .ai-chat-tab-label': {
              position: 'relative',
              zIndex: 1,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              lineHeight: 1,
              textTransform: 'uppercase',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
            },
          }}
        >
          <AutoAwesomeOutlinedIcon
            sx={{
              position: 'relative',
              zIndex: 1,
              boxSizing: 'content-box',
              p: 0.35,
              fontSize: 15,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.18)',
              border: '1px solid rgba(255, 255, 255, 0.28)',
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.24)',
            }}
          />
          <span className="ai-chat-tab-label">Chat</span>
        </Button>
      ) : null}

      <Paper
        ref={panelRef}
        elevation={0}
        role={isOpen ? 'dialog' : undefined}
        aria-label={isOpen ? 'AI chat panel' : undefined}
        aria-hidden={!isOpen}
        style={
          panelPosition
            ? {
                left: panelPosition.x,
                right: 'auto',
                top: panelPosition.y,
                transform: 'none',
              }
            : undefined
        }
        sx={{
          position: 'fixed',
          right: PANEL_DEFAULT_OFFSET,
          top: '50%',
          transform: 'translateY(-50%)',
          width: `min(${DEFAULT_PANEL_WIDTH}px, calc(100vw - ${PANEL_VIEWPORT_GUTTER}px))`,
          height: `min(${DEFAULT_PANEL_HEIGHT}px, calc(100dvh - ${PANEL_VIEWPORT_GUTTER}px))`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 3,
          border: 1,
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.32),
          bgcolor: 'background.paper',
          boxShadow: '0 24px 60px -16px rgba(15, 23, 42, 0.22), 0 8px 20px -10px rgba(15, 23, 42, 0.1)',
          zIndex: isOpen ? 1300 : -1,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          visibility: isOpen ? 'visible' : 'hidden',
        }}
      >
        <iframe
          ref={iframeRef}
          title="AI Ops chatbot"
          src={iframeSrc || currentEmbedUrl}
          allow="clipboard-write"
          onLoad={postAuthTokenAndContext}
          style={{
            border: 0,
            flex: 1,
            minHeight: 0,
            width: '100%',
          }}
        />
      </Paper>
    </>
  );
}
